import { Button, Stack, Typography, useTheme } from "@mui/material";
import { useAtomValue } from "jotai";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FixedFooter } from "web_myplann/Finances/Components/FixedFooter";
import { useFinishFinances } from "web_myplann/Finances/Hooks/useFinishFinances";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { CurrencyCode } from "web_myplann/Shared/constants";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { getBalanceColor } from "web_myplann/Shared/Utils/getBalanceColor";
import { signedNumberFormatter } from "web_myplann/Shared/Utils/numberFormatters";
import {
	DANGER_MYPLANN_DARK,
	DANGER_MYPLANN_LIGHT,
	SUCCESS_MYPLANN_DARK,
	SUCCESS_MYPLANN_LIGHT,
} from "web_myplann/Utils/constants";

export const FinancesOverview = () => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const { finishFinances, isPending } = useFinishFinances();
	const finance = useAtomValue(financeState);

	const balance =
		finance.income.reduce((acc, income) => acc + income.amount, 0) -
		finance.expenses.reduce((acc, expense) => acc + expense.monthlyPayment, 0);

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Finances.title)}
					defaultRoute={Routing.Finances.Expenses.path()}
				/>
			}
		>
			<Stack pt={3} alignItems="center">
				<Typography
					variant="deprecatedH3"
					color={theme.palette.deprecated.grey[900]}
				>
					{t(Resources.Finances.Overview.title)}
				</Typography>
				<Stack
					direction="row"
					alignItems="flex-end"
					justifyContent="center"
					py={5.6}
				>
					<Typography
						fontSize={40}
						fontWeight={700}
						color={getBalanceColor(balance, theme)}
					>
						{signedNumberFormatter.format(balance)}
					</Typography>
					<Stack pb={1} pl={1}>
						<Typography
							fontSize={16}
							fontWeight={700}
							color={getBalanceColor(balance, theme)}
						>
							{t(Resources.Common.CurrencySign)}
						</Typography>
					</Stack>
				</Stack>
			</Stack>
			<Stack p={1.6}>
				<Stack
					gap={1.2}
					bgcolor={theme.palette.deprecated.grey[100]}
					borderRadius={theme.radii.xl}
					p={1.6}
				>
					<Typography
						variant="deprecatedH3"
						color={theme.palette.deprecated.grey[900]}
						lineHeight={"140%"}
					>
						{balance > 0
							? t(Resources.Finances.Overview.balancePossitive, {
									balance: balance,
								})
							: t(Resources.Finances.Overview.balanceNegative, {
									balance: balance,
								})}
					</Typography>
					<Typography
						variant="deprecatedBody1"
						color={theme.palette.deprecated.grey[500]}
					>
						<Trans
							i18nKey={
								balance > 0
									? getResourcePath(
											Resources.Finances.Overview.balancePossitiveDescription,
										)
									: getResourcePath(
											Resources.Finances.Overview.balanceNegativeDescription,
										)
							}
							values={{
								balance: formatCurrency(Math.abs(balance), CurrencyCode.CZK),
							}}
							components={{
								balance: (
									<Typography
										component="span"
										fontSize={16}
										fontWeight={700}
										color={
											balance > 0
												? theme.palette.mode === "light"
													? SUCCESS_MYPLANN_DARK
													: SUCCESS_MYPLANN_LIGHT
												: theme.palette.mode === "light"
													? DANGER_MYPLANN_DARK
													: DANGER_MYPLANN_LIGHT
										}
									/>
								),
							}}
						/>
					</Typography>
				</Stack>
			</Stack>

			<FixedFooter gap={1.2}>
				<Button fullWidth onClick={finishFinances} loading={isPending}>
					{t(Resources.Finances.Overview.finishButton)}
				</Button>
				<Button
					variant="text"
					fullWidth
					onClick={() =>
						navigate(Routing.Finances.Income.path(), {
							state: {
								from: location.state?.from,
								wizardDepth: (location.state?.wizardDepth ?? 0) + 1,
							},
						})
					}
				>
					{t(Resources.Finances.Overview.checkButton)}
				</Button>
			</FixedFooter>
		</MyPlannLayout>
	);
};
